import type { Click, Movement, Technique as TechniqueType } from "@69ly/shared";
import { useTranslation } from "react-i18next";

const svg = { width: 20, height: 20, viewBox: "0 0 20 20", fill: "none", stroke: "currentColor", strokeWidth: 1.7, strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": true } as const;

/** How the player moves while throwing: still, crouched, walking (W) or running. */
export function MovementIcon({ movement }: { movement: Movement }) {
  switch (movement) {
    case "standing":
      return (
        <svg {...svg}>
          <circle cx="10" cy="3.6" r="1.9" />
          <path d="M10 6.2v5.8M10 7.6L6.8 10.2M10 7.6l3.2 2.6M10 12l-2.4 5.5M10 12l2.4 5.5" />
        </svg>
      );
    case "crouching":
      return (
        <svg {...svg}>
          <circle cx="8.6" cy="5.6" r="1.9" />
          <path d="M9.2 8.2l1.8 4.3M11 12.5l-3.6 1 .9 3.8M11 12.5l2.6 1.8-.4 3.2M10 9.6l3.6 1.6M4 17.5h12" />
        </svg>
      );
    case "walking":
      return (
        <svg {...svg}>
          <rect x="3" y="3" width="14" height="14" rx="3" />
          <path d="M6 7.4l1.5 5.4L10 9l2.5 3.8 1.5-5.4" />
        </svg>
      );
    case "running":
      return (
        <svg {...svg}>
          <circle cx="12.6" cy="3.6" r="1.9" />
          <path d="M11.6 6.6L9 11.6M9 11.6l3 2-1 3.9M9 11.6l-2.5 3-3 -.3M11.2 7.6l3.4 2M10.6 8L7 7.4" />
        </svg>
      );
  }
}

/** A jump throw, or none. */
export function JumpIcon({ jump }: { jump: boolean }) {
  return jump ? (
    <svg {...svg}>
      <path d="M10 14V3.5M6.5 7L10 3.5 13.5 7M4 17h12" />
    </svg>
  ) : (
    <svg {...svg}>
      <path d="M4 17h12M10 14V7" strokeDasharray="1.5 2.6" />
    </svg>
  );
}

/** A mouse with the button(s) to throw with filled. */
export function ClickIcon({ click }: { click: Click }) {
  return (
    <svg {...svg}>
      {(click === "left" || click === "both") && <path d="M10 2.5a5 5 0 00-5 5v1h5z" fill="currentColor" stroke="none" className="tech-fill" />}
      {(click === "right" || click === "both") && <path d="M10 2.5a5 5 0 015 5v1h-5z" fill="currentColor" stroke="none" className="tech-fill" />}
      <rect x="5" y="2.5" width="10" height="15" rx="5" />
      <path d="M10 2.5v6M5 8.5h10" />
    </svg>
  );
}

/**
 * How to throw it, at a glance: the movement, the jump and the click, each with
 * its icon and a small title. `compact` keeps only icons and words, on one line.
 */
export function Technique({ technique, compact = false }: { technique: TechniqueType; compact?: boolean }) {
  const { t } = useTranslation();
  const items = [
    { key: "movement", icon: <MovementIcon movement={technique.movement} />, value: t(`technique.movement.${technique.movement}`), off: false },
    { key: "jump", icon: <JumpIcon jump={technique.jump} />, value: technique.jump ? t("technique.jump.yes") : t("technique.jump.no"), off: !technique.jump },
    { key: "click", icon: <ClickIcon click={technique.click} />, value: t(`technique.click.${technique.click}`), off: false },
  ];
  return (
    <ul className={`technique${compact ? " is-compact" : ""}`} aria-label={t("technique.label")}>
      {items.map((it) => (
        <li key={it.key} className={`tech tech-${it.key}${it.off ? " is-off" : ""}`}>
          <span className="tech-icon">{it.icon}</span>
          <span className="tech-text">
            {!compact && <span className="tech-label">{t(`technique.labels.${it.key}`)}</span>}
            <span className="tech-value">{it.value}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}
